import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  AreaChart,
  PieChart,
  Line,
  Bar,
  Area,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from "recharts";
import { format } from "date-fns";
import { createFormatters } from "../helpers/dateFormatter";

export const public_labels = {
  date: "Date",
  active_user_count: "Active Users",
  user_count: "Total Users",
  tokens: "Tokens",
  blocked_calls: "Blocked Calls",
  avg_response_ms: "Avg Response (ms)",
  conversation_count: "Total Chats",
};

export const chartTheme = {
  colors: {
    primary: "#7C3AED",
    secondary: "#84D9FD",
    grid: "rgba(200,200,200,0.2)",
    axisText: "#4B5563",
    tooltipBg: "#FFFFFF",
    tooltipShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  margins: { top: 0, right: 20, bottom: 20, left: 0 },
  defaultHeight: 200,
};

// Make numbers compact to prevent Y Axis overflow (e.g. 10,000 -> 10k)
export const compactFmt = new Intl.NumberFormat("en", {
  notation: "compact",
  compactDisplay: "short",
}).format;

const COLORS = {
  projects: "#4F46E5", // Indigo
  chats: "#16A34A", // Green
  files: "#EA580C", // Orange
};

export function ChartWrapper({ height = chartTheme.defaultHeight, scroll = true, children }) {
  return (
    <div className="bg-white rounded-xl p-2">
      {/* Phones/tablets: the chart keeps a readable minimum width and scrolls
          sideways inside its card; from lg up it fits the card as before. */}
      <div className="table-scroll overflow-x-auto lg:overflow-visible">
        <div className={scroll ? "min-w-[520px] lg:min-w-0" : ""}>
          <ResponsiveContainer width="100%" height={height}>
            {children}
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const axisProps = {
  axisLine: false,
  tickLine: false,
  tick: {
    fill: chartTheme.colors.axisText,
    fontSize: 14,
  },
};

function GradientDefs({ id }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop
          offset="0%"
          stopColor={chartTheme.colors.primary}
          stopOpacity={0.8}
        />
        <stop
          offset="100%"
          stopColor={chartTheme.colors.primary}
          stopOpacity={0.1}
        />
      </linearGradient>
    </defs>
  );
}

export function StyledLineChart({ data, xKey, yKey, yAxisLabel }) {
  const windowSize = data.length;
  const { tickFormatter, labelFormatter } = createFormatters(xKey, windowSize);
  return (
    <ChartWrapper>
      <LineChart
        data={data}
        margin={{ ...chartTheme.margins, left: yAxisLabel ? 20 : chartTheme.margins.left }}
      >
        <GradientDefs id="primaryGradient" />
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chartTheme.colors.grid}
          vertical={false}
        />
        <XAxis
          dataKey={xKey}
          {...axisProps}
          tickMargin={12}
          tickFormatter={tickFormatter}
        />
        <YAxis
          tickMargin={12}
          {...axisProps}
          domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
          tickFormatter={(val) => compactFmt(val)}
          label={
            yAxisLabel
              ? {
                  value: yAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: { fill: chartTheme.colors.axisText, fontSize: 12, textAnchor: "middle" },
                }
              : undefined
          }
        />
        <Tooltip
          wrapperStyle={{
            backgroundColor: chartTheme.colors.tooltipBg,
            borderRadius: 8,
            boxShadow: chartTheme.colors.tooltipShadow,
          }}
          formatter={(value, name) => {
            const label = public_labels[name] ?? name;
            return [value, label];
          }}
          labelFormatter={labelFormatter}
        />
        <Line
          className="shadowed-line"
          type="monotone"
          dataKey={yKey}
          stroke={chartTheme.colors.primary}
          strokeWidth={3}
          dot={false}
        />
      </LineChart>
    </ChartWrapper>
  );
}

export function StyledAreaChart({ data, xKey, yKey, yAxisLabel }) {
  const windowSize = data.length;
  const { tickFormatter, labelFormatter } = createFormatters(xKey, windowSize);
  return (
    <ChartWrapper>
      <AreaChart
        data={data}
        margin={{ ...chartTheme.margins, left: yAxisLabel ? 20 : chartTheme.margins.left }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chartTheme.colors.grid}
          vertical={false}
        />
        <XAxis
          dataKey={xKey}
          {...axisProps}
          tickMargin={12}
          tickFormatter={tickFormatter}
        />
        <YAxis
          tickMargin={12}
          {...axisProps}
          domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
          tickFormatter={(val) => compactFmt(val)}
          label={
            yAxisLabel
              ? {
                  value: yAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: { fill: chartTheme.colors.axisText, fontSize: 12, textAnchor: "middle" },
                }
              : undefined
          }
        />
        <Tooltip
          wrapperStyle={{
            backgroundColor: chartTheme.colors.tooltipBg,
            borderRadius: 8,
            boxShadow: chartTheme.colors.tooltipShadow,
          }}
          formatter={(value, name) => {
            const label = public_labels[name] ?? name;
            return [value, label];
          }}
          labelFormatter={labelFormatter}
        />
        <Area
          type="monotone"
          dataKey={yKey}
          stroke={chartTheme.colors.primary}
          strokeWidth={3}
          fill="#7C3AED40"
          fillOpacity={1}
        />
      </AreaChart>
    </ChartWrapper>
  );
}

export function MultiLineChart({ data, xKey, lines }) {
  const windowSize = data.length;
  const { tickFormatter, labelFormatter } = createFormatters(xKey, windowSize);
  // Below laptop width the date labels collide, so thin them out there;
  // on laptops and larger the axis is exactly as before.
  const narrowTicks = useIsNarrow(1024);

  return (
    <ChartWrapper height={300}>
      <LineChart data={data} margin={chartTheme.margins}>
        <GradientDefs id="multiGradient" />
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chartTheme.colors.grid}
          vertical={false}
        />
        <XAxis
          dataKey={xKey}
          {...axisProps}
          tickMargin={12}
          tickFormatter={tickFormatter}
          {...(narrowTicks ? { interval: "preserveStartEnd", minTickGap: 40 } : {})}
        />
        <YAxis
          tickMargin={12}
          {...axisProps}
          domain={[0, (dataMax) => Math.ceil(dataMax * 2)]}
          tickFormatter={(val) => compactFmt(val)}
        />
        <Tooltip
          wrapperStyle={{
            backgroundColor: chartTheme.colors.tooltipBg,
            borderRadius: 8,
            boxShadow: chartTheme.colors.tooltipShadow,
          }}
          formatter={(value, name) => {
            const label = public_labels[name] ?? name;
            return [value, label];
          }}
          labelFormatter={labelFormatter}
        />
        {lines.map((line, index) => (
          <Line
            key={line.dataKey}
            className="shadowed-line"
            type="monotone"
            dataKey={line.dataKey}
            name={line.name}
            stroke={line.stroke}
            strokeWidth={3}
            dot={false}
          />
        ))}
        <Legend />
      </LineChart>
    </ChartWrapper>
  );
}

export function DualStyledLineChart({
  data,
  xKey,
  yKey1,
  yKey2,
  name1,
  name2,
}) {
  const windowSize = data.length;
  const { tickFormatter, labelFormatter } = createFormatters(xKey, windowSize);
  return (
    <ChartWrapper height={chartTheme.defaultHeight}>
      <LineChart
        data={data}
        margin={{
          top: chartTheme.margins.top,
          right: chartTheme.margins.right,
          left: chartTheme.margins.left,
          bottom: chartTheme.margins.bottom,
        }}
      >
        <GradientDefs id="dualGradient" />
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chartTheme.colors.grid}
          vertical={false}
        />
        <XAxis
          dataKey={xKey}
          {...axisProps}
          tickMargin={12}
          tickFormatter={tickFormatter}
        />
        <YAxis
          {...axisProps}
          tickMargin={12}
          tickFormatter={(val) => compactFmt(val)}
          domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
        />
        <Tooltip
          wrapperStyle={{
            backgroundColor: chartTheme.colors.tooltipBg,
            borderRadius: 8,
            boxShadow: chartTheme.colors.tooltipShadow,
          }}
          formatter={(value, name) => {
            const label = public_labels[name] ?? name;
            return [value, label];
          }}
          labelFormatter={labelFormatter}
        />
        <Line
          className="shadowed-line"
          type="monotone"
          dataKey={yKey1}
          name={name1}
          stroke={chartTheme.colors.primary}
          strokeWidth={3}
          dot={false}
        />
        <Line
          className="shadowed-line"
          type="monotone"
          dataKey={yKey2}
          name={name2}
          stroke={chartTheme.colors.secondary}
          strokeWidth={3}
          dot={false}
        />
        <Legend
          verticalAlign="bottom"
          align="center"
          iconType="circle"
          wrapperStyle={{
            position: "relative",
            top: -40,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "2rem",
            backgroundColor: "#F8FAFC",
            padding: "6px 16px",
            borderRadius: "9999px",
            border: "1px solid rgba(0,0,0,0)",
          }}
        />
      </LineChart>
    </ChartWrapper>
  );
}

export function StyledBarChart({ data, xKey, yKey, yAxisLabel }) {
  const windowSize = data.length;
  const { tickFormatter, labelFormatter } = createFormatters(xKey, windowSize);
  return (
    <ChartWrapper>
      <BarChart
        data={data}
        margin={{ ...chartTheme.margins, left: yAxisLabel ? 20 : chartTheme.margins.left }}
      >
        <defs>
          <filter
            id="bar-shadow"
            filterUnits="userSpaceOnUse"
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="2"
              floodColor="#000"
              floodOpacity="0.1"
            />
          </filter>
        </defs>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chartTheme.colors.grid}
          vertical={false}
        />
        <XAxis dataKey={xKey} {...axisProps} tickFormatter={tickFormatter} />

        <YAxis
          {...axisProps}
          tickFormatter={(val) => compactFmt(val)}
          label={
            yAxisLabel
              ? {
                  value: yAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                  style: { fill: chartTheme.colors.axisText, fontSize: 12, textAnchor: "middle" },
                }
              : undefined
          }
        />
        <Tooltip
          wrapperStyle={{
            backgroundColor: chartTheme.colors.tooltipBg,
            borderRadius: 8,
            boxShadow: chartTheme.colors.tooltipShadow,
          }}
          formatter={(value, name) => {
            const label = public_labels[name] ?? name;
            return [value, label];
          }}
          labelFormatter={labelFormatter}
        />
        <Bar
          dataKey={yKey}
          fill={chartTheme.colors.primary}
          barSize={12}
          filter="url(#bar-shadow)"
        />
      </BarChart>
    </ChartWrapper>
  );
}

export function DualStyledBarChart({ data, xKey, yKey1, yKey2, name1, name2 }) {
  const windowSize = data.length;
  const { tickFormatter, labelFormatter } = createFormatters(xKey, windowSize);
  return (
    <ChartWrapper height={chartTheme.defaultHeight}>
      <BarChart
        data={data}
        margin={{
          ...chartTheme.margins,
          bottom: chartTheme.margins.bottom,
        }}
      >
        <defs>
          <filter
            id="bar-shadow"
            filterUnits="userSpaceOnUse"
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="2"
              floodColor="#000"
              floodOpacity="0.1"
            />
          </filter>
        </defs>

        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chartTheme.colors.grid}
          vertical={false}
        />
        <XAxis dataKey={xKey} {...axisProps} tickFormatter={tickFormatter} />

        <YAxis {...axisProps} tickFormatter={(val) => compactFmt(val)} />

        <Tooltip
          wrapperStyle={{
            backgroundColor: chartTheme.colors.tooltipBg,
            borderRadius: 8,
            boxShadow: chartTheme.colors.tooltipShadow,
          }}
          formatter={(value, name) => {
            const label = public_labels[name] ?? name;
            return [value, label];
          }}
          labelFormatter={labelFormatter}
        />

        <Bar
          dataKey={yKey1}
          name={name1}
          fill={chartTheme.colors.primary}
          barSize={32}
          filter="url(#bar-shadow)"
          stackId="stack"
        />
        <Bar
          dataKey={yKey2}
          name={name2}
          fill={chartTheme.colors.secondary}
          barSize={32}
          filter="url(#bar-shadow)"
          stackId="stack"
        />
        <Legend
          verticalAlign="bottom"
          align="center"
          iconType="circle"
          wrapperStyle={{
            position: "relative",
            top: -40,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "2rem",
            backgroundColor: "#F8FAFC",
            padding: "6px 16px",
            borderRadius: "9999px",
            border: "1px solid rgba(0,0,0,0)",
          }}
        />
      </BarChart>
    </ChartWrapper>
  );
}

/**
 * Bar chart with one group of side-by-side bars per x value.
 * `bars` is [{ dataKey, name, fill }] — one entry per bar in each group.
 */
export function GroupedBarChart({ data, xKey, bars }) {
  const { tickFormatter, labelFormatter } = createFormatters(xKey, data.length);

  // Left to itself, recharts keeps a tick label only if its measured width
  // clears the previous one, so mixed label widths ("Jul 12" vs "Aug 1") thin
  // the axis unevenly. Choosing the step keeps every gap the same.
  // Phones/tablets: fewer date labels (and smaller text) so they never collide.
  // Laptops and larger keep the original 10 labels and sizes.
  const isPhone = useIsNarrow(640);
  const isTablet = useIsNarrow(1024);
  const maxTicks = isPhone ? 3 : isTablet ? 6 : 10;
  const tickInterval = Math.max(0, Math.ceil(data.length / maxTicks) - 1);
  const smallTick = { ...axisProps.tick, fontSize: 10 };

  return (
    <ChartWrapper height={isPhone ? 260 : 300}>
      <BarChart
        data={data}
        margin={isPhone ? { ...chartTheme.margins, right: 8 } : chartTheme.margins}
        barGap={4}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chartTheme.colors.grid}
          vertical={false}
        />
        <XAxis
          dataKey={xKey}
          {...axisProps}
          {...(isPhone ? { tick: smallTick } : {})}
          tickMargin={12}
          interval={tickInterval}
          tickFormatter={tickFormatter}
        />
        <YAxis
          tickMargin={12}
          {...axisProps}
          {...(isPhone ? { tick: smallTick, width: 30, tickMargin: 4 } : {})}
          allowDecimals={false}
          tickFormatter={(val) => compactFmt(val)}
        />
        <Tooltip
          cursor={{ fill: chartTheme.colors.grid }}
          wrapperStyle={{
            backgroundColor: chartTheme.colors.tooltipBg,
            borderRadius: 8,
            boxShadow: chartTheme.colors.tooltipShadow,
          }}
          formatter={(value, name) => {
            const label = public_labels[name] ?? name;
            return [value, label];
          }}
          labelFormatter={labelFormatter}
        />
        {bars.map((bar) => (
          <Bar
            key={bar.dataKey}
            dataKey={bar.dataKey}
            name={bar.name}
            fill={bar.fill}
            maxBarSize={24}
            radius={[4, 4, 0, 0]}
          />
        ))}
        <Legend
          iconSize={isPhone ? 10 : 14}
          wrapperStyle={isPhone ? { fontSize: 11, paddingTop: 6 } : undefined}
        />
      </BarChart>
    </ChartWrapper>
  );
}

export function StyledPieChart({
  data,
  dataKey,
  nameKey,
  colors,
  height: customHeight,
  outerRadius: customOuterRadius,
  variant = "donut", // "donut" | "pie" — "pie" drops the inner radius to 0
  legendPosition = "bottom", // "bottom" | "right"
}) {
  const valueKey = dataKey;
  const total = data.reduce((sum, entry) => sum + entry[valueKey], 0);
  // Phones get a smaller pie, a shorter chart and a stacked legend so nothing overlaps.
  const isPhone = useIsNarrow(640);
  const baseOuterR = customOuterRadius || 60;
  const outerR = isPhone ? Math.min(baseOuterR, 80) : baseOuterR;
  const innerR = variant === "pie" ? 0 : Math.round(outerR * 0.42);

  const legendItems = data.map((entry, i) => ({
    label: entry[nameKey],
    percent: total ? Math.round((entry[valueKey] / total) * 100) : 0,
    color: colors?.[i] || chartTheme.colors.primary,
  }));

  const renderLegendBottom = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexWrap: "wrap",
        background: "#F8FAFC",
        padding: isPhone ? "10px 12px" : "6px 16px",
        borderRadius: isPhone ? 16 : "9999px",
        marginTop: 16,
        gap: isPhone ? "10px 16px" : 24,
      }}
    >
      {legendItems.map((item) => (
        <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: item.color,
              display: "inline-block",
            }}
          />
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontSize: 12, color: "#333" }}>{item.label}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>{item.percent}%</div>
          </div>
        </div>
      ))}
    </div>
  );

  // Vertical stacked list, used when the legend sits beside the chart instead
  // of below it — a horizontal pill doesn't fit a narrower side column.
  const renderLegendRight = () => (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 14, minWidth: isPhone ? 0 : 160, maxWidth: "100%" }}>
      {legendItems.map((item) => (
        <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: item.color,
              display: "inline-block",
              flexShrink: 0,
            }}
          />
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{item.label}</div>
            <div style={{ fontSize: 12, color: "#94A3B8" }}>{item.percent}%</div>
          </div>
        </div>
      ))}
    </div>
  );

  const chart = (
    <ChartWrapper height={isPhone ? Math.min(customHeight || 300, 240) : customHeight || 300} scroll={false}>
      <PieChart margin={{ top: 0, right: 20, bottom: legendPosition === "right" ? 10 : 40, left: 0 }}>
        <defs>
          <filter
            id="pie-shadow"
            filterUnits="userSpaceOnUse"
            x="-20%"
            y="-20%"
            width="140%"
            height="140%"
          >
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="2"
              floodColor="#000"
              floodOpacity="0.1"
            />
          </filter>
        </defs>
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          outerRadius={outerR}
          innerRadius={innerR}
          filter="url(#pie-shadow)"
        >
          {data.map((entry, i) => (
            <Cell
              key={`cell-${i}`}
              fill={colors?.[i] || chartTheme.colors.primary}
            />
          ))}
        </Pie>
        <Tooltip
          wrapperStyle={{
            backgroundColor: chartTheme.colors.tooltipBg,
            borderRadius: 8,
            boxShadow: chartTheme.colors.tooltipShadow,
          }}
          formatter={(value, name) => {
            const percent = total ? Math.round((value / total) * 100) : 0;
            return [`${value} (${percent}%)`, name];
          }}
        />
      </PieChart>
    </ChartWrapper>
  );

  if (legendPosition === "right") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: isPhone ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          gap: isPhone ? 16 : 40,
        }}
      >
        <div style={{ width: isPhone ? "100%" : outerR * 2 + 60, maxWidth: "100%", flexShrink: 0 }}>{chart}</div>
        {renderLegendRight()}
      </div>
    );
  }

  return (
    <>
    {chart}
    {renderLegendBottom()}
    </>
  );
}

export function CategoryBarChart({ data, dataKey, nameKey, colors }) {
  const valueKey = dataKey;
  const nameField = nameKey;

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[300px] text-gray-500">
        No category data available
      </div>
    );
  }

  // Sort data by value (highest first) for better visualization
  const sortedData = [...data].sort((a, b) => b[valueKey] - a[valueKey]);

  // Calculate max value for proper scaling
  const maxValue = Math.max(...sortedData.map((item) => item[valueKey]));

  return (
    <ChartWrapper height={400}>
      <BarChart
        data={sortedData}
        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey={nameField}
          angle={-45}
          textAnchor="end"
          height={80}
          tick={{ fontSize: 11, fill: "#374151" }}
        />
        <YAxis
          tick={{ fontSize: 12, fill: "#374151" }}
          domain={[0, maxValue + 1]}
        />
        <Bar dataKey={valueKey} fill="#8884d8">
          {sortedData.map((entry, index) => {
            // Alternate between purple and blue colors
            const color = index % 2 === 0 ? "#8B5CF6" : "#3B82F6"; // Purple and Blue alternating
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Bar>
      </BarChart>
    </ChartWrapper>
  );
}

/**
 * Horizontal ranked bar chart for a single measure across many (5+) named
 * categories. Deliberately single-hue rather than one color per category:
 * each bar already carries its own text label, so color here encodes
 * magnitude, not identity — see CategoryBarChart for the identity case.
 * Horizontal + sorted descending keeps long category names legible without
 * rotating axis labels.
 */
// True on phone-sized screens, so charts can shrink their fixed label areas
// there. On larger screens everything keeps its original size.
function useIsNarrow(breakpoint = 640) {
  const [narrow, setNarrow] = React.useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  React.useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return narrow;
}

export function CategoryRankBarChart({ data, dataKey, nameKey, height, barColors }) {
  const valueKey = dataKey;
  const nameField = nameKey;
  const isNarrow = useIsNarrow();

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[240px] text-sm text-slate-400">
        No category data available
      </div>
    );
  }

  const sortedData = [...data].sort((a, b) => b[valueKey] - a[valueKey]);
  const total = sortedData.reduce((sum, item) => sum + (item[valueKey] || 0), 0);
  const rowHeight = 34;

  return (
    <ChartWrapper height={height || Math.max(160, sortedData.length * rowHeight)}>
      <BarChart
        data={sortedData}
        layout="vertical"
        margin={{ top: 4, right: isNarrow ? 36 : 64, left: 8, bottom: 4 }}
        barCategoryGap={10}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={chartTheme.colors.grid}
          horizontal={false}
        />
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey={nameField}
          width={isNarrow ? 90 : 150}
          {...axisProps}
          tick={{ fill: chartTheme.colors.axisText, fontSize: isNarrow ? 11 : 12 }}
        />
        <Tooltip
          cursor={{ fill: chartTheme.colors.grid }}
          wrapperStyle={{
            backgroundColor: chartTheme.colors.tooltipBg,
            borderRadius: 8,
            boxShadow: chartTheme.colors.tooltipShadow,
          }}
          formatter={(value) => {
            const pct = total ? Math.round((value / total) * 1000) / 10 : 0;
            return [`${value.toLocaleString()} · ${pct}%`, "Interactions"];
          }}
        />
        <Bar
          dataKey={valueKey}
          fill={chartTheme.colors.primary}
          maxBarSize={22}
          radius={[0, 4, 4, 0]}
        >
          {barColors && sortedData.map((_, i) => (
            <Cell key={i} fill={barColors[Math.min(i, barColors.length - 1)]} />
          ))}
          <LabelList
            dataKey={valueKey}
            position="right"
            // Deliberately just the count, no " · pct%" — Recharts wraps a
            // LabelList's text using the BAR's own width as the line budget,
            // not the actual free space past it, so a short bar (small value)
            // force-wraps a longer label even though the chart has room. A
            // bare number never contains the whitespace it would wrap on.
            // The exact share is still one hover away via the Tooltip above.
            formatter={(value) => compactFmt(value)}
            style={{ fill: "#334155", fontSize: 12, fontVariantNumeric: "tabular-nums" }}
          />
        </Bar>
      </BarChart>
    </ChartWrapper>
  );
}

export function StyledLineChartV2({ data, xKey, yKeys }) {
  if (!data || data.length === 0) {
    return (
      <div className="h-[300px] flex items-center justify-center text-sm text-gray-400">
        No data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        {/* X Axis → exact dates from API */}
        <XAxis
          dataKey={xKey}
          tickFormatter={(value) =>
            new Date(value).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
            })
          }
        />

        {/* Y Axis → fixed scale */}
        <YAxis
          ticks={[0, 20, 40, 60, 80]}
          domain={[0, 80]}
          allowDecimals={false}
        />

        <Tooltip />
        <Legend />

        {/* Multiple colored lines */}
        {yKeys.map(({ key, label }) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            name={label}
            stroke={COLORS[key]}
            strokeWidth={3}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function mergeAnalyticsByDate(metrics) {
  const map = {};

  metrics.forEach(({ key, source, valueKey }) => {
    source.forEach((item) => {
      if (!map[item.date]) {
        map[item.date] = { date: item.date };
      }
      map[item.date][key] = item[valueKey] || 0;
    });
  });

  return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
}

/**
 * Reusable chart card
 */
// `variant="design"` is the updated dashboard look (light border, bold navy title,
// divider above the totals); the default look is unchanged for the other pages.
export function AnalyticsChartCard({ title, subtitle, chartData, lines, variant }) {
  const isDesign = variant === "design";
  const isEmpty =
    !chartData.length ||
    chartData.every((item) =>
      lines.every((line) => (item[line.dataKey] || 0) === 0)
    );

  /**
   * Calculate totals per line
   */
  const totals = lines.map((line) => {
    const total = chartData.reduce(
      (sum, item) => sum + (item[line.dataKey] || 0),
      0
    );

    return {
      label: line.name,
      value: total,
      color: line.stroke,
    };
  });

  return (
    <div
      className={
        isDesign
          ? "min-w-0 w-full rounded-2xl border border-[#ECEEF4] bg-white p-5 sm:p-6"
          : "min-w-0 w-full rounded-xl border bg-white p-6 shadow-sm"
      }
    >
      {/* Header */}
      <div className="mb-4">
        <h2
          className={
            isDesign
              ? "text-base font-bold tracking-tight text-[#14122B]"
              : "text-lg font-semibold text-slate-900"
          }
        >
          {title}
        </h2>
        {(!isDesign || subtitle) && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>

      {/* Chart / Empty */}
      {isEmpty ? (
        <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
          No activity in this time range
        </div>
      ) : (
        <>
          <MultiLineChart data={chartData} xKey="date" lines={lines} />

          {/* Totals */}
          <div className={isDesign ? "mt-4 border-t border-[#ECEEF4] pt-4" : "mt-4 border-t pt-4"}>
            <div className={isDesign ? "flex flex-wrap gap-x-6 gap-y-2" : "flex flex-wrap gap-6"}>
              {totals.map((total) => (
                <div
                  key={total.label}
                  className="flex items-center gap-2 text-sm"
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: total.color }}
                  />
                  <span className="text-slate-600">Total {total.label}:</span>
                  <span className="font-semibold text-slate-900">
                    {total.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
